import { Avatar, Button, Stack, Typography } from '@mui/material';
import { ITherapySuppliersResult, getSuppliers } from 'API/Suppliers/suppliers';
import { NoResults2 } from 'Components';
import { SuppliersContext } from 'Context/SuppliersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { SUPPLIER_CATEGORY } from 'Enum/suppliers';
import { useDictionary } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { ArrowBackBlue } from 'Images';
import { Therapy } from 'Modules/Suppliers/SuppliersMain/ResultsPages/Results/Mapper/Therapy';
import { onlyNumbers } from 'Regex';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

export const Supplier = () => {
  const { filters, setFilters } = useContext(SuppliersContext);
  const { supplierCategoryId, supplierSpecialty, supplierId } = useParams();
  const [supplier, setSupplier] = useState<ITherapySuppliersResult[]>([]);
  const { loader, setIsLoading, isLoading } = useLoader();

  const navigate = useNavigate();

  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers);

  const initSupplier = useCallback(async () => {
    setIsLoading(true);
    if (
      supplierSpecialty &&
      supplierId &&
      onlyNumbers.test(supplierSpecialty) &&
      onlyNumbers.test(supplierId)
    ) {
      const supplierResult = await getSuppliers(
        Number(supplierSpecialty),
        supplierId
      );
      const specialty = Number(supplierSpecialty);
      if (supplierResult) setSupplier(supplierResult);
      setFilters({ ...filters, specialty });
    }
    setIsLoading(false);
  }, [supplierSpecialty, supplierId, setIsLoading, setFilters]);

  useEffect(() => {
    initSupplier();
  }, [initSupplier]);

  const mapResultsToComponent = (
    result: ITherapySuppliersResult[],
    Component: React.ComponentType<any>
  ) => {
    return <Component data={result[0]} isAlwaysOpen={true} />;
  };

  const genericResultHandler = () => {
    if (supplier.length === 0) return <NoResults2 />;
    switch (supplierCategoryId?.toLocaleLowerCase()) {
      case SUPPLIER_CATEGORY.MENTAL_THERAPY:
        return mapResultsToComponent(supplier, Therapy);
      default:
        return null;
    }
  };

  const genericTitleHandler = () => {
    switch (supplierCategoryId?.toLocaleLowerCase()) {
      case SUPPLIER_CATEGORY.MENTAL_THERAPY:
        return dictionary.lobby.categories.therapy;
      default:
        return <NoResults2 />;
    }
  };

  return (
    <>
      <Helmet
        title={
          supplier.length > 0
            ? `${dictionary.title} - ${supplier[0].name}`
            : dictionary.title
        }
      />
      {isLoading ? (
        loader
      ) : (
        <Stack
          alignItems='center'
          sx={{ maxWidth: '800px', m: '24px auto 0 auto' }}
        >
          <Typography variant='h3' sx={{ textAlign: 'center', mb: '16px' }}>
            {genericTitleHandler()}
          </Typography>
          {genericResultHandler()}
          <Button
            onClick={() => navigate('/suppliers/therapy')}
            sx={{
              mt: '32px',
              maxWidth: '210px',
              p: '10px 16px',
              height: 'fit-content',
              borderRadius: '100px',
              border: '1px solid',
              borderColor: 'primary.shade_400',
              display: 'flex',
              bgcolor: '#FFF',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant='body4'>{dictionary.findSuppliers}</Typography>
            <Avatar
              alt=''
              src={ArrowBackBlue}
              sx={{ width: '16px', height: '16px' }}
            />
          </Button>
        </Stack>
      )}
    </>
  );
};
